/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the AI tutor side panel.
 * @state-owner student UI store; navigation store
 * @allowed-imports React; same-module panel and store; assistants module public API; shared components and hooks; role provider
 * @forbidden teacher-only controls; direct API client
 * @public-api via modules/activities/index.ts
 * @side-effects scrolls the message list to the newest entry
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useEffect, useRef, useState } from 'react';
import { useActiveRole } from '../../../app/providers/ActiveRoleProvider';
import { PanelResizer } from '../../../shared/components/Resizer';
import { CloseIcon } from '../../../shared/components/VispoIcon';
import { ME } from '../../../shared/lib/demo-data';
import { findRun } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useIsMobile, useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { assistantForRun } from '../../assistants';
import {
  assistantSpeaks,
  policyNotice,
  resolvePolicy,
  selectOpenSession,
  selectOverrides,
  studentSeesTeacher,
  useAgentPolicy,
  useInterventions,
} from '../../live';
import { useStudentUiStore } from '../store/student-ui-store';
import { TutorPanel } from './TutorPanel';

export function TutorThread() {
  const t = useTranslator();
  const role = useActiveRole();
  const mobile = useIsMobile();
  const section = useWorkspaceUiStore((state) => state.section);
  const selection = useWorkspaceUiStore((state) => state.selection);
  const threadOpen = useStudentUiStore((state) => state.threadOpen);
  const toggleThread = useStudentUiStore((state) => state.toggleThread);
  const thread = useStudentUiStore((state) => state.thread);
  const containerRef = useRef<HTMLElement>(null);
  const runs = useDatasetStore((state) => state.runs);
  const assistants = useDatasetStore((state) => state.assistants);
  const run = findRun(runs, selection);
  const assistant = assistantForRun(assistants, run?.assistantId);
  const sessions = useInterventions((state) => state.sessions);
  const patchSession = useInterventions((state) => state.patch);
  const session = selectOpenSession(sessions, ME);
  const overrides = useAgentPolicy((state) => state.overrides);
  /** A mode change the student cannot see is a mode change they cannot ask about. */
  const notice = run
    ? policyNotice(resolvePolicy(selectOverrides(overrides, run.id), ME).policy)
    : null;
  const [restated, setRestated] = useState('');

  const show = role === 'student' && section === 'act' && Boolean(selection) && threadOpen;

  // biome-ignore lint/correctness/useExhaustiveDependencies: `thread` is the trigger — a new message must scroll the list
  useEffect(() => {
    if (!show) return;
    const list = containerRef.current?.querySelector('#ms');
    if (list) list.scrollTop = list.scrollHeight;
  }, [show, thread]);

  if (!show) return <aside className="thr" id="thr" style={{ display: 'none' }} />;

  return (
    <aside
      className={`thr${mobile ? ' open' : ''}`}
      id="thr"
      ref={containerRef}
      style={{ display: 'flex' }}
    >
      <PanelResizer max={560} min={300} panel="thr" side="l" />
      {/* The student is told the teacher is here, and the assistant stops competing for the turn. */}
      {session && studentSeesTeacher(session.mode) ? (
        <div className="thjoin">
          <b>{t('老師已加入')}</b>
          <span>
            {assistantSpeaks(session.mode) ? t('助教繼續陪你') : t('助教先暫停，改由老師回你')}
          </span>
        </div>
      ) : null}
      {notice ? <div className="thmode">{t(notice)}</div> : null}
      <div className="cvh">
        {/* The teacher picks the persona when they assign the run, so the student sees that one. */}
        <div
          className={`av ${assistant ? 'asav2' : 'g'}`}
          style={assistant ? { ['--accent' as string]: assistant.accent } : undefined}
        >
          {assistant ? assistant.emoji : t('助')}
        </div>
        <div className="t">
          <b>{assistant ? assistant.name : 'AI 助教'}</b>
          <span>{assistant?.tagline || t('陪你寫這一題')}</span>
        </div>
        <button className="ib" type="button" onClick={toggleThread}>
          <CloseIcon />
        </button>
      </div>
      <TutorPanel slim />
      {session?.askRestate && !session.endedAt ? (
        <div className="thrs">
          <b>{t('用你自己的話說一次')}</b>
          <span>{t('說得出來才代表真的懂了，說完老師就把你交回助教。')}</span>
          <textarea
            onChange={(event) => setRestated(event.target.value)}
            placeholder={t('例如：移項的時候符號要相反')}
            rows={2}
            value={restated}
          />
          <button
            className="bt sm pri"
            disabled={!restated.trim()}
            type="button"
            onClick={() => {
              patchSession(session.id, { restatement: restated.trim() });
              setRestated('');
            }}
          >
            {t('送出')}
          </button>
        </div>
      ) : null}
    </aside>
  );
}
